import { useEffect } from "react";
import { useMap } from "react-map-gl/mapbox";

/**
 * Single source of truth for the stacking order of every layer the app adds
 * on top of the base map, across all maps (risks map, municipality risks map,
 * municipality portfolio map). Ordered bottom → top.
 *
 * Entries are either an exact layer id (string) or `{ prefix }`, which matches
 * every id starting with that prefix (for layers generated per tileset or per
 * map variant). Layers not listed here are left untouched; listed layers are
 * kept above them in this order.
 */
export const MAP_LAYER_ORDER = [
  // Hazard fills
  { prefix: "fire-low-layer-" }, // fire terrain tiles, low zoom
  { prefix: "fire-layer-" }, // fire terrain tiles, high zoom
  "fire-hazard-layer-fill",
  "earthquake-hazard-layer-fill",
  { prefix: "flood-hazard-" }, // flood hazard tilesets, one layer per zoom range

  // Water features, above the hazard fills
  "flood-lakes-layer",
  "flood-rivers-layer",

  // Earthquake line features
  "earthquake-tectonic-plates-layer",
  "earthquake-active-faults-layer",

  // Historical (past-event) overlays — kept above all hazard fills
  "historical-fire-fill-layer",
  "historical-fire-outline-layer",
  "historical-flood-layer",
  "historical-flood-count-layer",
  "historical-earthquake-layer",

  // Fire assessment buffer polygons — under the drawn/history polygons so the
  // blue polygon fills stay crisp on top of the translucent buffer.
  "fire-buffer-fill",
  "fire-buffer-outline",

  // History area-assessment polygons (kept above the fire buffer).
  "history-area-fill",
  "history-area-line",

  // mapbox-gl-draw layers
  { prefix: "gl-draw-" },

  // Municipality boundary (portfolio + risks variants)
  { prefix: "municipalities-layer-fill" },
  { prefix: "municipalities-layer-line" },

  // Assessment overlays: flood distance lines/markers, fire radius circle,
  // distance labels
  "flood-coast-line-layer",
  "flood-stream-line-layer",
  "flood-coast-marker-layer",
  "flood-stream-marker-layer",
  "circle-fill",
  "circle-outline",
  "flood-coast-label-layer",
  "flood-stream-label-layer",

  // Mask hiding everything outside the allowed municipalities stays topmost
  { prefix: "municipalities-outside-mask-layer" },
];

const getLayerRank = (layerId) =>
  MAP_LAYER_ORDER.findIndex((entry) =>
    typeof entry === "string"
      ? entry === layerId
      : layerId.startsWith(entry.prefix),
  );

/**
 * Keeps every layer listed in MAP_LAYER_ORDER stacked in that order, no matter
 * when React mounts/unmounts them (e.g. re-enabling the flood hazard layer
 * while rivers are visible would otherwise put it above them).
 *
 * Must be rendered as a child of a react-map-gl <Map>. react-map-gl only
 * renders Map children once the map instance exists, so useMap() is reliably
 * populated here — unlike an outer ref, which is still null while the map is
 * created asynchronously.
 */
export function MapLayerOrderEnforcer() {
  const { current: mapRef } = useMap();

  useEffect(() => {
    const map = mapRef?.getMap();
    if (!map) return undefined;

    const enforceOrder = () => {
      // getStyle() throws while the style is still loading; skip — the idle
      // and load listeners re-trigger enforcement once the map settles.
      if (!map.isStyleLoaded()) return;

      const styleLayers = map.getStyle()?.layers;
      if (!styleLayers) return;

      const managed = styleLayers
        .map((layer) => ({ id: layer.id, rank: getLayerRank(layer.id) }))
        .filter((layer) => layer.rank !== -1);

      const desired = [...managed].sort((a, b) => a.rank - b.rank);
      if (managed.every((layer, index) => layer.id === desired[index].id)) {
        return;
      }

      // Move each managed layer to the very top, bottom-most first, so they
      // all end up above the base style layers in the configured order.
      desired.forEach(({ id }) => map.moveLayer(id));
    };

    enforceOrder();

    // styledata fires whenever a layer is added or removed. Reordering is
    // deferred to the next frame and skipped when already sorted, so the
    // styledata events triggered by moveLayer itself don't cause a loop.
    const reassert = () => requestAnimationFrame(enforceOrder);
    map.on("styledata", reassert);
    map.on("load", reassert);
    // styledata events during style loading are skipped above, so also
    // re-check when the map goes idle — it fires once things settle and the
    // already-sorted check makes it a cheap no-op.
    map.on("idle", reassert);

    return () => {
      map.off("styledata", reassert);
      map.off("load", reassert);
      map.off("idle", reassert);
    };
  }, [mapRef]);

  return null;
}
